import classnames from 'classnames';
import { isNil } from 'ramda';
import React from 'react';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import getColWidth, { DEFAULT_WIDTH, GridColWidths } from '../Core/getColWidth';
import './Table.scss';

interface ITableProps {
  colHeaders?: (string | JSX.Element)[];
  colWidths?: GridColWidths;
  minColWidths?: GridColWidths;
  cornerHeader?: string | JSX.Element;
  data?: any[][];
  hiddenRows?: number[];
  rowHeaders?: (string | JSX.Element)[];
  rowHeaderWidth?: number;
  alternateRowShading?: boolean;
}

/**
 * This implementation follows the API for the Handsontable Table API. Try to keep them consistent for ease of use.
 * https://handsontable.com/docs/6.2.2/Options.html
 */
const TableBase: React.FC<ITableProps> = ({
  colHeaders,
  colWidths,
  minColWidths,
  cornerHeader,
  data,
  hiddenRows,
  rowHeaders,
  rowHeaderWidth,
  alternateRowShading = false,
}) => {
  // Row / col helpers
  const showColumnHeaders = colHeaders && colHeaders.length > 0;
  const showRowHeaders = rowHeaders && rowHeaders.length > 0;
  const isHiddenRow = (rowIndex: number) => !!(hiddenRows && hiddenRows.includes(rowIndex));

  // all widths / heights are done in JS so we can set them based on properties set by the parent
  const TableCellHeight = 34;

  const isAlternateRow = (rowIndex: number): boolean => alternateRowShading && !!rowIndex && !!(rowIndex % 2);
  const getTableCellStyle = (colIndex: number) => ({
    height: TableCellHeight,
    width: getColWidth(colWidths, colIndex),
    minWidth: isNil(minColWidths) ? undefined : getColWidth(minColWidths, colIndex),
  });

  const tableCellRowHeaderStyle = { height: TableCellHeight, width: rowHeaderWidth ? rowHeaderWidth : DEFAULT_WIDTH };

  return (
    <div className="Table">
      <table>
        <tbody>
          {showColumnHeaders && (
            <tr className="Table-Row">
              {showRowHeaders && (
                <td className="Table-Cell Table-Cell-rowHeader" style={tableCellRowHeaderStyle}>
                  {cornerHeader}
                </td>
              )}

              {colHeaders?.map((colHeader, colIndex) => (
                <td className="Table-Cell Table-Cell-colHeader" style={getTableCellStyle(colIndex)} key={colIndex}>
                  {colHeader}
                </td>
              ))}
            </tr>
          )}
          {data?.map(
            (row, rowIndex) =>
              !isHiddenRow(rowIndex) && (
                <tr className="Table-Row" key={rowIndex}>
                  {showRowHeaders && (
                    <td className="Table-Cell Table-Cell-rowHeader" style={tableCellRowHeaderStyle} key={rowIndex}>
                      {rowHeaders?.[rowIndex] !== undefined && rowHeaders[rowIndex] !== null
                        ? rowHeaders[rowIndex]
                        : null}
                    </td>
                  )}

                  {row.map((col, colIndex) => (
                    <td
                      className={classnames('Table-Cell', {
                        'Table-Cell-alternateRow': isAlternateRow(rowIndex),
                      })}
                      style={getTableCellStyle(colIndex)}
                      key={colIndex}
                    >
                      {col}
                    </td>
                  ))}
                </tr>
              )
          )}
        </tbody>
      </table>
    </div>
  );
};

const Table = withInjectedProps(TableBase);

export { Table };
export default Table;
